/**
 * Recipe Data Transformation
 * Maps WordPress REST API and JSON file data to React Recipe interface
 */
/**
 * Transform API/JSON recipe data to React Recipe interface
 */
export function transformRecipe(source, flameLevel) {
    if (!source) {
        return null;
    }
    // Determine flame level (use provided, or from source, or default to 2)
    const targetFlame = flameLevel || source.flame || 2;
    // Get flame-specific ingredients and steps
    const ingredients = getFlameSpecificIngredients(source, targetFlame);
    const steps = getFlameSpecificSteps(source, targetFlame);
    // Extract image URL
    const imageUrl = extractImageUrl(source);
    // Extract servings
    const servings = source.yield_base_servings || 4;
    const baseServings = servings;
    // Calculate times
    const cookTime = source.cook_time_total_minutes || 0;
    const prepTime = source.prep_time_minutes || 0;
    const totalTime = cookTime + prepTime || cookTime;
    // Transform recipe
    const recipe = {
        id: source.recipe_id || source.slug || '',
        slug: source.slug || source.recipe_id || '',
        name: source.title || '',
        summary: source.summary || '',
        image: imageUrl,
        cookTime,
        prepTime,
        totalTime,
        servings,
        baseServings,
        flameLevel: targetFlame,
        ingredients,
        equipment: transformEquipment(source.equipment),
        prepAndSafety: transformSafetyNotes(source.safety_notes),
        steps,
        notes: transformNotes(source.notes),
        variations: transformVariations(source.variations || []),
        sides: transformSides(source.sides || []),
        leftover: source.leftover ? transformSideRecipe(source.leftover) : undefined,
        badges: source.badges || [],
        glossaryTerms: transformGlossaryTerms(source.glossary_terms),
        nutrition: transformNutrition(source.nutrition),
    };
    return recipe;
}
/**
 * Get flame-specific ingredients
 * Returns DetailedRecipeIngredient[] (new format)
 */
function getFlameSpecificIngredients(source, flameLevel) {
    let ingredients = [];
    // Try flame-specific array first
    if (flameLevel === 1 && source.ingredients_1f) {
        ingredients = source.ingredients_1f;
    }
    else if (flameLevel === 2 && source.ingredients_2f) {
        ingredients = source.ingredients_2f;
    }
    else if (flameLevel === 3 && source.ingredients_3f) {
        ingredients = source.ingredients_3f;
    }
    else {
        // Fallback: try other flame levels in order
        if (source.ingredients_2f && source.ingredients_2f.length > 0) {
            ingredients = source.ingredients_2f;
        }
        else if (source.ingredients_1f && source.ingredients_1f.length > 0) {
            ingredients = source.ingredients_1f;
        }
        else if (source.ingredients_3f && source.ingredients_3f.length > 0) {
            ingredients = source.ingredients_3f;
        }
    }
    // Transform to DetailedRecipeIngredient format (preserve API structure)
    return ingredients.map((ing, index) => ({
        id: ing.id || `ing-${index}`,
        item: ing.item,
        ingredient: ing.ingredient,
        amount: ing.amount,
        unit: ing.unit,
        metric_g: ing.metric_g,
        metric_ml: ing.metric_ml,
        weight_g: ing.weight_g,
        note: ing.note,
    }));
}
/**
 * Get flame-specific steps
 */
function getFlameSpecificSteps(source, flameLevel) {
    let steps = [];
    // Try flame-specific array first
    if (flameLevel === 1 && source.steps_1f) {
        steps = source.steps_1f;
    }
    else if (flameLevel === 2 && source.steps_2f) {
        steps = source.steps_2f;
    }
    else if (flameLevel === 3 && source.steps_3f) {
        steps = source.steps_3f;
    }
    else {
        // Fallback: try other flame levels in order
        if (source.steps_2f && source.steps_2f.length > 0) {
            steps = source.steps_2f;
        }
        else if (source.steps_1f && source.steps_1f.length > 0) {
            steps = source.steps_1f;
        }
        else if (source.steps_3f && source.steps_3f.length > 0) {
            steps = source.steps_3f;
        }
    }
    // Transform to RecipeStep format
    return steps.map((step, index) => {
        var _a;
        const stepNumber = typeof step.id === 'number' ? step.id : step.stepNumber || index + 1;
        const instruction = step.instruction || step.method || '';
        return {
            id: ((_a = step.id) === null || _a === void 0 ? void 0 : _a.toString()) || `step-${index}`,
            stepNumber,
            instruction,
            hasImage: step.hasImage || false,
            imageUrl: step.imageUrl,
            timer: step.timer,
            timerLabel: step.timerLabel,
        };
    });
}
/**
 * Extract image URL from source
 */
function extractImageUrl(source) {
    // WP REST API returns image object
    if ('image' in source && source.image && typeof source.image === 'object') {
        return source.image.full || source.image.catalog || source.image.thumbnail || '';
    }
    // JSON files may have image as string
    if ('image' in source && typeof source.image === 'string') {
        return source.image;
    }
    return '';
}
/**
 * Transform variations
 */
function transformVariations(variations) {
    if (!Array.isArray(variations)) {
        return [];
    }
    // Basic transformation - may need adjustment based on actual variation structure
    return variations.map((variation, index) => ({
        id: variation.id || `variation-${index}`,
        name: variation.name || variation.title || '',
        description: variation.description || '',
        flameLevel: (variation.flameLevel || variation.flame || 2),
        ingredientChanges: variation.ingredientChanges || [],
        stepChanges: variation.stepChanges || [],
        timeAdjustment: variation.timeAdjustment,
    }));
}
/**
 * Transform sides
 */
function transformSides(sides) {
    if (!Array.isArray(sides)) {
        return [];
    }
    return sides.map((side) => transformSideRecipe(side));
}
/**
 * Transform a single side recipe
 */
function transformSideRecipe(side) {
    // If it's already a full recipe object, extract what we need
    const flame = (side.flame || 2);
    return {
        id: side.recipe_id || side.slug || side.id || '',
        slug: side.slug || side.recipe_id || '',
        name: side.title || side.name || '',
        summary: side.summary || '',
        image: extractImageUrl(side),
        cookTime: side.cook_time_total_minutes || 0,
        prepTime: side.prep_time_minutes || 0,
        servings: side.yield_base_servings || 4,
        flameLevel: flame,
        ingredients: getFlameSpecificIngredients(side, flame),
        steps: getFlameSpecificSteps(side, flame),
    };
}
/**
 * Transform nutrition data
 */
function transformNutrition(nutrition) {
    if (!nutrition) {
        return {
            calories: 0,
            protein: '0g',
            carbs: '0g',
            fat: '0g',
            fiber: '0g',
        };
    }
    return {
        calories: nutrition.calories || 0,
        protein: nutrition.protein || '0g',
        carbs: nutrition.carbs || '0g',
        fat: nutrition.fat || '0g',
        fiber: nutrition.fiber || '0g',
    };
}
/**
 * Transform equipment array - handles both string[] and object[] formats
 * API returns: [{tool: "name", role: "...", alternatives: [...]}]
 * Component expects: string[]
 */
function transformEquipment(equipment) {
    if (!equipment || !Array.isArray(equipment)) {
        return [];
    }
    return equipment.map((item) => {
        // If it's already a string, return it
        if (typeof item === 'string') {
            return item;
        }
        // If it's an object with 'tool' property, extract it
        if (item && typeof item === 'object' && item.tool) {
            return item.tool;
        }
        // Fallback: try other property names
        if (item && typeof item === 'object') {
            return item.name || item.equipment || '';
        }
        return '';
    }).filter((item) => item !== '');
}
/**
 * Transform safety notes array - handles both string[] and object[] formats
 */
function transformSafetyNotes(safetyNotes) {
    if (!safetyNotes || !Array.isArray(safetyNotes)) {
        return [];
    }
    return safetyNotes.map((item) => {
        if (typeof item === 'string') {
            return item;
        }
        // If it's an object, try to extract text content
        if (item && typeof item === 'object') {
            return item.text || item.note || item.message || item.content || '';
        }
        return '';
    }).filter((item) => item !== '');
}
/**
 * Transform notes array - handles both string[] and object[] formats
 */
function transformNotes(notes) {
    if (!notes || !Array.isArray(notes)) {
        return [];
    }
    return notes.map((item) => {
        if (typeof item === 'string') {
            return item;
        }
        // If it's an object, try to extract text content
        if (item && typeof item === 'object') {
            return item.text || item.note || item.message || item.content || '';
        }
        return '';
    }).filter((item) => item !== '');
}
/**
 * Transform glossary terms array - handles both string[] and object[] formats
 * API returns: [{key: "term-slug", override_definition: "..."}]
 * Component expects: string[]
 */
function transformGlossaryTerms(glossaryTerms) {
    if (!glossaryTerms || !Array.isArray(glossaryTerms)) {
        return [];
    }
    return glossaryTerms.map((item) => {
        // If it's already a string, return it
        if (typeof item === 'string') {
            return item;
        }
        // If it's an object with 'key' property (from API), extract it
        if (item && typeof item === 'object' && item.key) {
            return item.key;
        }
        // Fallback: try other common property names
        if (item && typeof item === 'object') {
            return item.term || item.name || item.slug || '';
        }
        return '';
    }).filter((item) => item !== '');
}
